import config from '@config';
import RefreshIcon from '@icons/refresh.svg?react';
import { OutlinedButton as Button } from '@ui/buttons';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import Styled from './ChatError.styled';

const SUPPORT_EMAIL = 'support@gwi.com';

interface Props {
  connectionErrorCount: number;
  naturalLanguageErrorCount: number;
  onRetry: () => void;
}

function ChatError({
  connectionErrorCount,
  naturalLanguageErrorCount,
  onRetry
}: Props) {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    console.table({
      ['Connection errors']: connectionErrorCount,
      ['Natural language errors']: naturalLanguageErrorCount
    });
    setShouldShow(connectionErrorCount > 0 || naturalLanguageErrorCount > 0);
  }, [connectionErrorCount, naturalLanguageErrorCount]);

  if (!shouldShow) {
    return null;
  }

  let errorMessage: ReactNode = null;

  switch (naturalLanguageErrorCount) {
    case 1:
      errorMessage = (
        <span>I&apos;m having trouble completing your request right now.</span>
      );
      break;
    case 2:
      errorMessage = (
        <span>
          I&apos;m still having trouble. This usually resolves quickly. Try
          again in a moment, or start a new conversation.
        </span>
      );
      break;
    default:
      errorMessage = (
        <span>
          The service you&apos;re trying to use isn't available right now.{' '}
          <span>
            Please{' '}
            <Styled.Link
              data-testid="chat-error-support-link"
              href={`mailto:${SUPPORT_EMAIL}?subject=Octopus%20support%20request`}
              title="Ask for help">
              contact support
            </Styled.Link>{' '}
            if this continues.
          </span>
        </span>
      );
  }

  if (connectionErrorCount > 0) {
    errorMessage = (
      <span>No internet connection. Check your network and try again.</span>
    );
  }

  const handleRetry = () => {
    setShouldShow(false);
    onRetry();
  };

  return (
    <Styled.Wrapper>
      <Styled.Text data-testid="chat-error-message">{errorMessage}</Styled.Text>
      {(connectionErrorCount > 0 ||
        naturalLanguageErrorCount < config.prompt.error.maxCount) && (
        <Button
          testId="chat-error-retry-button"
          startIcon={<RefreshIcon />}
          onClick={handleRetry}>
          Try again
        </Button>
      )}
    </Styled.Wrapper>
  );
}

export default ChatError;
